import { datadogLogs } from "@datadog/browser-logs";
import { useRequiredContext } from "@redotech/react-util/context";
import { useWindowEventListener } from "@redotech/react-util/event";
import { supportedFreightMarketplaces } from "@redotech/redo-fulfillment-carrier-common/freight";
import { personalizedSlipsAccessible } from "@redotech/redo-fulfillment-common/personalized-slip/personalized-slip-access";
import { RedoShopifyDeliveryMethodType } from "@redotech/redo-fulfillment-group-common/fulfillment-delivery-method-type";
import {
  FulfillmentGroupData,
  FulfillmentGroupPickStatus,
} from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import {
  canPurchaseLabelStatus,
  FulfillmentGroupStatus,
  isDoneStatus,
  OpenLikeStatuses,
} from "@redotech/redo-fulfillment-group-common/fulfillment-group-status";
import { FulfillmentGroupActions } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import { Provider } from "@redotech/redo-order-common/provider";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import {
  RedoButtonSize,
  RedoButtonTheme,
} from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoButtonDropdown } from "@redotech/redo-web/arbiter-components/buttons/redo-dropdown-button";
import {
  RedoCommandMenu,
  RedoCommandMenuItem,
} from "@redotech/redo-web/arbiter-components/command-menu/redo-command-menu";
import {
  ReloadTeamContext,
  TeamContext,
} from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { getDateString } from "@redotech/util/date-format";
import { browserIsMac } from "@redotech/web-util/browser";
import { memo, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useFedexAgreement } from "../hooks/use-fedex-agreement";
import { useUpsAgreement } from "../hooks/use-ups-agreement";
import { isConsolidationShipment } from "../utils/consolidation-invoice-utils";
import { useAllowedActions } from "../utils/use-allowed-actions";
import { FedexAgreementModal } from "./fedex-agreement-modal";
import { PrintBatchContext } from "./print-batch-modal";
import { UpsAgreementModal } from "./ups-agreement-modal";

const isPrintOption: Record<FulfillmentGroupActions, boolean> = {
  [FulfillmentGroupActions.MARK_AS_PICKED_UP]: false,
  [FulfillmentGroupActions.MARK_AS_FULFILLED]: false,
  [FulfillmentGroupActions.READY_FOR_PICKUP]: false,
  [FulfillmentGroupActions.PRINT_LABEL]: true,
  [FulfillmentGroupActions.PRINT_PACKING_SLIP]: true,
  [FulfillmentGroupActions.PRINT_PERSONALIZED_SLIP]: true,
  [FulfillmentGroupActions.PRINT_PICK_LIST]: true,
  [FulfillmentGroupActions.PRINT_ORDER_SUMMARY]: true,
  [FulfillmentGroupActions.PRINT_LOCAL_PICKUP_LABEL]: true,
  [FulfillmentGroupActions.CREATE_AND_PRINT_LABEL]: true,
  [FulfillmentGroupActions.CREATE_LABEL]: false,
  [FulfillmentGroupActions.COMMERCIAL_INVOICE]: false,
  [FulfillmentGroupActions.PLACE_HOLD]: false,
  [FulfillmentGroupActions.RELEASE_HOLD]: false,
  [FulfillmentGroupActions.UNMERGE]: false,
  [FulfillmentGroupActions.ADD_FUNDS_AND_CREATE]: false,
  [FulfillmentGroupActions.EDIT_ORDER]: false,
  [FulfillmentGroupActions.NOTIFY_MARKETPLACE]: false,
  [FulfillmentGroupActions.SUBMIT_CARRIER_CLAIM]: false,
  [FulfillmentGroupActions.CREATE_RETURN_LABEL]: false,
  [FulfillmentGroupActions.PRINT_RETURN_LABEL]: true,
  [FulfillmentGroupActions.EXPORT_PICK_LIST_CSV]: false,
  [FulfillmentGroupActions.OVERRIDE_NOT_RECEIVED]: false,
  [FulfillmentGroupActions.RESOLVE_PROBLEM_ORDER]: false,
  [FulfillmentGroupActions.BOOK_FREIGHT]: false,
  [FulfillmentGroupActions.PRINT_BOL]: true,
  [FulfillmentGroupActions.CREATE_PURCHASE_ORDER]: false,
  [FulfillmentGroupActions.VIEW_PURCHASE_ORDER]: false,
  [FulfillmentGroupActions.SCAN_AND_VERIFY]: false,
  [FulfillmentGroupActions.PRINT_PARCEL_PACKING_SLIPS]: true,
};

const displayButtonTheme: Record<FulfillmentGroupActions, RedoButtonTheme> = {
  [FulfillmentGroupActions.CREATE_AND_PRINT_LABEL]: "success",
  [FulfillmentGroupActions.CREATE_LABEL]: "success",
  [FulfillmentGroupActions.MARK_AS_PICKED_UP]: "success",
  [FulfillmentGroupActions.MARK_AS_FULFILLED]: "success",
  [FulfillmentGroupActions.READY_FOR_PICKUP]: "action",
  [FulfillmentGroupActions.PRINT_LABEL]: "normal",
  [FulfillmentGroupActions.PRINT_PACKING_SLIP]: "normal",
  [FulfillmentGroupActions.PRINT_PERSONALIZED_SLIP]: "normal",
  [FulfillmentGroupActions.PRINT_PICK_LIST]: "normal",
  [FulfillmentGroupActions.PRINT_ORDER_SUMMARY]: "normal",
  [FulfillmentGroupActions.PRINT_LOCAL_PICKUP_LABEL]: "normal",
  [FulfillmentGroupActions.COMMERCIAL_INVOICE]: "normal",
  [FulfillmentGroupActions.PLACE_HOLD]: "normal",
  [FulfillmentGroupActions.RELEASE_HOLD]: "normal",
  [FulfillmentGroupActions.UNMERGE]: "normal",
  [FulfillmentGroupActions.ADD_FUNDS_AND_CREATE]: "success",
  [FulfillmentGroupActions.EDIT_ORDER]: "success",
  [FulfillmentGroupActions.NOTIFY_MARKETPLACE]: "success",
  [FulfillmentGroupActions.SUBMIT_CARRIER_CLAIM]: "normal",
  [FulfillmentGroupActions.CREATE_RETURN_LABEL]: "normal",
  [FulfillmentGroupActions.PRINT_RETURN_LABEL]: "normal",
  [FulfillmentGroupActions.EXPORT_PICK_LIST_CSV]: "success",
  [FulfillmentGroupActions.OVERRIDE_NOT_RECEIVED]: "normal",
  [FulfillmentGroupActions.RESOLVE_PROBLEM_ORDER]: "normal",
  [FulfillmentGroupActions.BOOK_FREIGHT]: "success",
  [FulfillmentGroupActions.PRINT_BOL]: "normal",
  [FulfillmentGroupActions.CREATE_PURCHASE_ORDER]: "normal",
  [FulfillmentGroupActions.VIEW_PURCHASE_ORDER]: "normal",
  [FulfillmentGroupActions.SCAN_AND_VERIFY]: "normal",
  [FulfillmentGroupActions.PRINT_PARCEL_PACKING_SLIPS]: "normal",
};

const FulfillmentGroupActionKeyboardShortcuts: Partial<{
  [key in FulfillmentGroupActions]: string;
}> = { [FulfillmentGroupActions.CREATE_AND_PRINT_LABEL]: "p" };

// Providers whose holds must be released in the marketplace, not in Redo.
const HOLD_RELEASE_UNSUPPORTED_PROVIDERS: ReadonlySet<Provider> = new Set([
  Provider.LINNWORKS_TRACKSTAR,
]);

const MERCHANT_ACCOUNT_CLAIM_DISABLED_TOOLTIP =
  "This label was purchased on your own carrier account. Redo can only file claims for shipments with Redo shipping insurance or Redo carrier labels. Sign up for Reclaim Shipping to cover labels like this, or file directly with your carrier.";

// Which buttons to show is getting complicated, better to go one by one
const getOptions = ({
  deliveryMethod,
  canPurchaseEveryGroup,
  canHoldEveryGroup,
  selectedStatus,
  errorMessage,
  printCommercialInvoice,
  unmerge,
  insufficientFunds,
  canEditManualOrder,
  canNotifyMarketplace,
  canSubmitCarrierClaim,
  hasSubmittedCarrierClaim,
  carrierClaimMerchantAccountBlocked,
  canCreateReturnLabel,
  canPrintReturnLabel,
  defaultActionButton,
  hasPersonalizedSlips,
  localPickupLabelEnabled,
  fulfillmentGroups,
  canPurchaseSomeGroups,
  imsEnabled,
  packingParcelsEnabled,
  isConsolidationWithParcels,
  marketplaceHoldReleaseUnsupported,
}: {
  deliveryMethod: RedoShopifyDeliveryMethodType | null | undefined;
  selectedStatus: FulfillmentGroupStatus | null | undefined;
  canPurchaseEveryGroup: boolean;
  canHoldEveryGroup: boolean;
  errorMessage: boolean;
  printCommercialInvoice: boolean;
  unmerge: boolean;
  insufficientFunds: boolean;
  canEditManualOrder: boolean;
  canNotifyMarketplace: boolean;
  canSubmitCarrierClaim: boolean;
  hasSubmittedCarrierClaim: boolean;
  carrierClaimMerchantAccountBlocked: boolean;
  canCreateReturnLabel: boolean;
  canPrintReturnLabel: boolean;
  defaultActionButton?: FulfillmentGroupActions;
  hasPersonalizedSlips: boolean;
  localPickupLabelEnabled: boolean;
  fulfillmentGroups: readonly FulfillmentGroupData[];
  canPurchaseSomeGroups: boolean;
  imsEnabled: boolean;
  packingParcelsEnabled: boolean;
  isConsolidationWithParcels: boolean;
  marketplaceHoldReleaseUnsupported: boolean;
}) => {
  let options: FulfillmentGroupActions[] = [];
  // CREATE_AND_PRINT
  // CREATE
  if (
    deliveryMethod === RedoShopifyDeliveryMethodType.Shipping &&
    !errorMessage &&
    canPurchaseSomeGroups
  ) {
    options.push(FulfillmentGroupActions.CREATE_AND_PRINT_LABEL);
    options.push(FulfillmentGroupActions.CREATE_LABEL);
  }
  // PRINT_LABEL
  if (
    deliveryMethod === RedoShopifyDeliveryMethodType.Shipping &&
    selectedStatus === FulfillmentGroupStatus.Closed &&
    !errorMessage
  ) {
    options.push(FulfillmentGroupActions.PRINT_LABEL);
  }
  // MARK_AS_PICKED_UP
  if (
    deliveryMethod === RedoShopifyDeliveryMethodType.PickUp &&
    selectedStatus === FulfillmentGroupStatus.ReadyForPickup
  ) {
    options.push(FulfillmentGroupActions.MARK_AS_PICKED_UP);
  }
  // READY_FOR_PICKUP
  if (
    deliveryMethod === RedoShopifyDeliveryMethodType.PickUp &&
    canPurchaseEveryGroup
  ) {
    options.push(FulfillmentGroupActions.READY_FOR_PICKUP);
  }
  // BOOK_FREIGHT
  if (
    fulfillmentGroups.length === 1 &&
    deliveryMethod === RedoShopifyDeliveryMethodType.Freight &&
    canPurchaseLabelStatus(fulfillmentGroups[0]?.status) &&
    fulfillmentGroups[0]?.providers?.every((p) =>
      supportedFreightMarketplaces.includes(p),
    )
  ) {
    options.push(FulfillmentGroupActions.BOOK_FREIGHT);
  }
  // PRINT_BOL
  if (
    fulfillmentGroups.length === 1 &&
    selectedStatus === FulfillmentGroupStatus.Closed &&
    fulfillmentGroups[0]?.shipmentDetails?.freightShipment
  ) {
    options.push(FulfillmentGroupActions.PRINT_BOL);
  }
  // ADD_FUNDS
  if (
    deliveryMethod === RedoShopifyDeliveryMethodType.Shipping &&
    canPurchaseSomeGroups &&
    insufficientFunds
  ) {
    options.push(FulfillmentGroupActions.ADD_FUNDS_AND_CREATE);
  }
  // MARK_AS_FULFILLED
  if (
    selectedStatus &&
    !isDoneStatus(selectedStatus) &&
    selectedStatus !== FulfillmentGroupStatus.OnHold
  ) {
    options.push(FulfillmentGroupActions.MARK_AS_FULFILLED);
  }
  // PRINT_PACKING_SLIP
  options.push(FulfillmentGroupActions.PRINT_PACKING_SLIP);
  // PRINT_PARCEL_PACKING_SLIPS
  if (isConsolidationWithParcels) {
    options.push(FulfillmentGroupActions.PRINT_PARCEL_PACKING_SLIPS);
  }
  // PRINT_PERSONALIZED_SLIP
  if (hasPersonalizedSlips) {
    options.push(FulfillmentGroupActions.PRINT_PERSONALIZED_SLIP);
  }
  // EXPORT_PICK_LIST_CSV
  options.push(FulfillmentGroupActions.EXPORT_PICK_LIST_CSV);
  // PRINT_PICK_LIST
  options.push(FulfillmentGroupActions.PRINT_PICK_LIST);
  // PRINT_ORDER_SUMMARY
  options.push(FulfillmentGroupActions.PRINT_ORDER_SUMMARY);
  // PRINT_LOCAL_PICKUP_LABEL
  if (localPickupLabelEnabled) {
    options.push(FulfillmentGroupActions.PRINT_LOCAL_PICKUP_LABEL);
  }
  // COMMERCIAL_INVOICE
  if (printCommercialInvoice) {
    options.push(FulfillmentGroupActions.COMMERCIAL_INVOICE);
  }
  // PLACE_HOLD
  if (canPurchaseEveryGroup && canHoldEveryGroup) {
    options.push(FulfillmentGroupActions.PLACE_HOLD);
  }

  // RELEASE_HOLD — not supported when any group's hold is owned by the
  // marketplace (a marketplace group with no Redo-placed hold; release there)
  if (
    selectedStatus === FulfillmentGroupStatus.OnHold &&
    !marketplaceHoldReleaseUnsupported
  ) {
    options.push(FulfillmentGroupActions.RELEASE_HOLD);
  }

  // OVERRIDE_NOT_RECEIVED
  const allGroupsNotReceived = fulfillmentGroups.every(
    (group) => group.status === FulfillmentGroupStatus.OpenNotReceived,
  );
  if (allGroupsNotReceived) {
    options.push(FulfillmentGroupActions.OVERRIDE_NOT_RECEIVED);
  }

  // UNMERGE
  if (unmerge) {
    options.push(FulfillmentGroupActions.UNMERGE);
  }

  // EDIT_ORDER
  if (canEditManualOrder && selectedStatus && !isDoneStatus(selectedStatus)) {
    options.push(FulfillmentGroupActions.EDIT_ORDER);
  }

  // NOTIFY_MARKETPLACE
  if (canNotifyMarketplace) {
    options.push(FulfillmentGroupActions.NOTIFY_MARKETPLACE);
  }

  // SUBMIT_CARRIER_CLAIM — submitted or merchant-account-blocked variants are
  // handled in the rendering step below.
  if (
    canSubmitCarrierClaim ||
    hasSubmittedCarrierClaim ||
    carrierClaimMerchantAccountBlocked
  ) {
    options.push(FulfillmentGroupActions.SUBMIT_CARRIER_CLAIM);
  }

  // CREATE_RETURN_LABEL
  if (canCreateReturnLabel) {
    options.push(FulfillmentGroupActions.CREATE_RETURN_LABEL);
  }

  // PRINT_RETURN_LABEL
  if (canPrintReturnLabel) {
    options.push(FulfillmentGroupActions.PRINT_RETURN_LABEL);
  }

  if (
    fulfillmentGroups.length === 1 &&
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    fulfillmentGroups[0].pickStatus === FulfillmentGroupPickStatus.PROBLEM
  ) {
    options.push(FulfillmentGroupActions.RESOLVE_PROBLEM_ORDER);
  }

  // CREATE_PURCHASE_ORDER / VIEW_PURCHASE_ORDER
  if (fulfillmentGroups.length === 1 && imsEnabled) {
    options.push(
      fulfillmentGroups[0]?.linkedPurchaseOrderId
        ? FulfillmentGroupActions.VIEW_PURCHASE_ORDER
        : FulfillmentGroupActions.CREATE_PURCHASE_ORDER,
    );
  }

  // SCAN_AND_VERIFY
  if (fulfillmentGroups.length === 1 && packingParcelsEnabled) {
    options.push(FulfillmentGroupActions.SCAN_AND_VERIFY);
  }

  if (defaultActionButton && options.includes(defaultActionButton)) {
    options = [
      defaultActionButton,
      ...options.filter((action) => action !== defaultActionButton),
    ];
  }
  return options;
};

export const FulfillmentGroupActionsButton = memo(
  function FulfillmentGroupActionsButton({
    onClick,
    fulfillmentGroups,
    loading,
    displayedButtonAction,
    selectedStatus,
    canPurchaseEveryGroup,
    purchasableGroups,
    errorMessage,
    insufficientFunds,
    warningMessage,
    printShortcutsDisabled,
    setActionButtonExternalRef,
    onActionButtonClick,
    everythingDisabled,
    buttonLabels,
    buttonSize,
    useDefaultActionButton,
    squared,
    dropdownClassName,
    viewSubmittedClaim,
  }: {
    onClick: (value: FulfillmentGroupActions) => void;
    loading: boolean;
    fulfillmentGroups: readonly FulfillmentGroupData[];
    displayedButtonAction: FulfillmentGroupActions;
    selectedStatus: FulfillmentGroupStatus | null | undefined;
    canPurchaseEveryGroup: boolean;
    purchasableGroups?: readonly FulfillmentGroupData[];
    errorMessage?: string;
    insufficientFunds: boolean;
    warningMessage?: string;
    printShortcutsDisabled?: boolean;
    onActionButtonClick?: () => void;
    setActionButtonExternalRef?: (ref: HTMLButtonElement | null) => void;
    everythingDisabled: boolean;
    buttonLabels?: Partial<Record<FulfillmentGroupActions, string>>;
    buttonSize?: RedoButtonSize;
    useDefaultActionButton?: boolean;
    squared?: boolean;
    dropdownClassName?: string;
    viewSubmittedClaim?: (claimId: string) => void;
  }) {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [upsAgreementModalOpen, setUpsAgreementModalOpen] = useState(false);
    const [fedexAgreementModalOpen, setFedexAgreementModalOpen] =
      useState(false);
    const [localLoading, setLocalLoading] = useState(false);
    const loadingTimeoutRef = useRef<NodeJS.Timeout>();
    const reloadTeam = useContext(ReloadTeamContext);
    const batchPrinting = useContext(PrintBatchContext);

    const { shouldShowUpsAgreementLink, isButtonDisabledByUpsAgreement } =
      useUpsAgreement();

    const { shouldShowFedexAgreementLink } = useFedexAgreement();

    const team = useRequiredContext(TeamContext);
    const isClosed = selectedStatus === FulfillmentGroupStatus.Closed;

    const deliveryMethod = fulfillmentGroups.every(
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      (fg) => fg.deliveryMethod === fulfillmentGroups[0].deliveryMethod,
    )
      ? // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        fulfillmentGroups[0].deliveryMethod
      : null;

    const {
      canNotifyMarketplace,
      canHoldEveryGroup,
      canSubmitCarrierClaim,
      carrierClaimMerchantAccountBlocked,
      submittedShippingClaim,
      canEditManualOrder,
      canCreateReturnLabel,
      canPrintReturnLabel,
    } = useAllowedActions(fulfillmentGroups);

    const defaultActionButton = useMemo(() => {
      // If there is a problem order - that should override the default action button
      if (
        fulfillmentGroups.some(
          (fg) => fg.pickStatus === FulfillmentGroupPickStatus.PROBLEM,
        )
      ) {
        return FulfillmentGroupActions.RESOLVE_PROBLEM_ORDER;
      }
      if (useDefaultActionButton) {
        return team.settings.outboundLabels.defaultActionButton;
      }
      return undefined;
    }, [
      useDefaultActionButton,
      team.settings.outboundLabels.defaultActionButton,
      fulfillmentGroups,
    ]);

    const canPurchaseSomeGroups = (purchasableGroups?.length ?? 0) > 0;

    const marketplaceHoldReleaseUnsupported = fulfillmentGroups.some(
      (fg) =>
        fg.providers?.some((provider) =>
          HOLD_RELEASE_UNSUPPORTED_PROVIDERS.has(provider),
        ) &&
        !fg.holds?.some(
          (hold) =>
            !hold.provider ||
            !HOLD_RELEASE_UNSUPPORTED_PROVIDERS.has(hold.provider),
        ),
    );

    const { dropdownItems, actionByShortcut } = useMemo(() => {
      const options = getOptions({
        deliveryMethod,
        selectedStatus,
        canPurchaseEveryGroup,
        canHoldEveryGroup,
        errorMessage: !!errorMessage,
        printCommercialInvoice: fulfillmentGroups.some(
          (fg) => fg.shipment?.label?.commercialInvoiceUrl,
        ),
        unmerge: fulfillmentGroups.every(
          (fg) =>
            fg.fulfillmentOrderIds.length > 1 &&
            OpenLikeStatuses.includes(fg.status),
        ),
        insufficientFunds,
        canEditManualOrder,
        canNotifyMarketplace,
        canSubmitCarrierClaim,
        hasSubmittedCarrierClaim: submittedShippingClaim !== null,
        carrierClaimMerchantAccountBlocked,
        canCreateReturnLabel,
        canPrintReturnLabel,
        defaultActionButton,
        hasPersonalizedSlips:
          personalizedSlipsAccessible(team.settings.outboundLabels) &&
          fulfillmentGroups.some((fg) => fg.personalizedSlipTemplateId),
        localPickupLabelEnabled:
          !!team.settings?.outboundLabels?.documents?.localPickupLabel?.enabled,
        fulfillmentGroups,
        canPurchaseSomeGroups,
        imsEnabled: !!team.settings.ims?.enabled,
        packingParcelsEnabled:
          team.settings.outboundLabels?.wms?.packingParcelsEnabled ?? false,
        isConsolidationWithParcels:
          fulfillmentGroups.length === 1 &&
          !!fulfillmentGroups[0] &&
          isConsolidationShipment(fulfillmentGroups[0]) &&
          (fulfillmentGroups[0].packingParcels?.length ?? 0) > 0,
        marketplaceHoldReleaseUnsupported,
      });

      const shortcutsInUse = new Set<string>();
      const shortcutMap = new Map<string, FulfillmentGroupActions>();
      let nextNumericShortcut = 1;

      const items: RedoCommandMenuItem[] = options.map((option) => {
        let shortcut = FulfillmentGroupActionKeyboardShortcuts[option];
        if (!shortcut || shortcutsInUse.has(shortcut)) {
          shortcut = nextNumericShortcut.toString();
          nextNumericShortcut++;
          shortcutMap.set(`Digit${shortcut}`, option);
        } else {
          shortcutsInUse.add(shortcut);
          shortcutMap.set(shortcut, option);
        }

        const submittedClaimForRow =
          option === FulfillmentGroupActions.SUBMIT_CARRIER_CLAIM &&
          !canSubmitCarrierClaim
            ? submittedShippingClaim
            : null;

        const carrierClaimBlockedRow =
          option === FulfillmentGroupActions.SUBMIT_CARRIER_CLAIM &&
          !canSubmitCarrierClaim &&
          !submittedClaimForRow &&
          carrierClaimMerchantAccountBlocked;

        return {
          dataTestId: `fulfillment-group-actions-${option}`,
          text: submittedClaimForRow
            ? `Carrier claim submitted on ${getDateString(submittedClaimForRow.submittedAt)}.`
            : (buttonLabels?.[option] ?? option),
          tooltip: submittedClaimForRow
            ? `Carrier claim submitted on ${getDateString(submittedClaimForRow.submittedAt)}. Click to view claim detail.`
            : undefined,
          disabledTooltip: carrierClaimBlockedRow
            ? MERCHANT_ACCOUNT_CLAIM_DISABLED_TOOLTIP
            : undefined,
          Icon: () => (
            <RedoBadge
              color="gray"
              size="xs"
              text={`${browserIsMac() ? "⌥" : "Alt+"}${shortcut.toUpperCase()}`}
            />
          ),
          onClick: () => {
            // Log rapid dropdown option clicks
            const now = Date.now();
            if (now - lastDropdownClickRef.current < 500) {
              console.error("Detected rapid click on dropdown option", {
                timeSinceLastClick: now - lastDropdownClickRef.current,
                option,
              });
            }
            lastDropdownClickRef.current = now;

            if (submittedClaimForRow) {
              viewSubmittedClaim?.(submittedClaimForRow.id);
              return;
            }

            onClick(option);
          },
          disabled:
            carrierClaimBlockedRow ||
            ([
              FulfillmentGroupActions.CREATE_AND_PRINT_LABEL,
              FulfillmentGroupActions.PRINT_LABEL,
              FulfillmentGroupActions.PRINT_PACKING_SLIP,
              FulfillmentGroupActions.PRINT_PERSONALIZED_SLIP,
              FulfillmentGroupActions.PRINT_PICK_LIST,
              FulfillmentGroupActions.PRINT_ORDER_SUMMARY,
              FulfillmentGroupActions.PRINT_PARCEL_PACKING_SLIPS,
            ].includes(option)
              ? batchPrinting.disabled
              : false),
          id: option,
        };
      });

      return { dropdownItems: items, actionByShortcut: shortcutMap };
      // FIXME
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      isClosed,
      onClick,
      deliveryMethod,
      selectedStatus,
      errorMessage,
      fulfillmentGroups,
      canEditManualOrder,
      canHoldEveryGroup,
      canPurchaseEveryGroup,
      canPurchaseSomeGroups,
      canNotifyMarketplace,
      canSubmitCarrierClaim,
      carrierClaimMerchantAccountBlocked,
      submittedShippingClaim,
      viewSubmittedClaim,
      insufficientFunds,
      batchPrinting.disabled,
      marketplaceHoldReleaseUnsupported,
    ]);

    const [actionButtonRef, setActionButtonRef] =
      useState<HTMLButtonElement | null>(null);
    const [containerRef, setContainerRef] = useState<HTMLElement | null>(null);
    const lastKeyPressRef = useRef<number>(0);
    const lastDropdownClickRef = useRef<number>(0);

    useEffect(() => {
      setActionButtonExternalRef?.(actionButtonRef);
    }, [actionButtonRef, setActionButtonExternalRef]);

    useEffect(() => {
      return () => {
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
        }
      };
    }, []);

    useWindowEventListener(window, "keydown", (e) => {
      if (printShortcutsDisabled) {
        return;
      }

      const target = e.target as HTMLElement | null;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable
      ) {
        return;
      }

      const key = e.key.toLowerCase();

      // Handle Alt/Option shortcuts for list items
      if (e.altKey) {
        const action =
          actionByShortcut.get(key) ?? actionByShortcut.get(e.code);

        if (action) {
          e.preventDefault();

          // Log potential double-triggers from keyboard shortcut
          const now = Date.now();
          if (now - lastKeyPressRef.current < 500) {
            console.error("Detected rapid keyboard shortcut trigger", {
              timeSinceLastPress: now - lastKeyPressRef.current,
              action,
            });
          }
          lastKeyPressRef.current = now;

          onClick(action);
          setDropdownOpen(false);
        }
        return;
      }

      // Handle Cmd/Ctrl shortcuts for non-numeric actions (work when list is closed)
      if ((e.metaKey || e.ctrlKey) && key === "p") {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        const action = dropdownItems[0].id as FulfillmentGroupActions;
        e.preventDefault();

        // Log potential double-triggers from keyboard shortcut
        const now = Date.now();
        if (now - lastKeyPressRef.current < 500) {
          console.error("Detected rapid keyboard shortcut trigger", {
            timeSinceLastPress: now - lastKeyPressRef.current,
            action,
          });
        }
        lastKeyPressRef.current = now;

        onClick(action);

        // Close list if it was open
        if (dropdownOpen) {
          setDropdownOpen(false);
        }
      }
    });

    const ShortcutBadge = (
      <RedoBadge
        color="gray"
        size="xs"
        text={`${browserIsMac() ? "⌘" : "Ctrl+"}P`}
      />
    );

    const actionDisabled =
      everythingDisabled ||
      selectedStatus === FulfillmentGroupStatus.Pending ||
      isButtonDisabledByUpsAgreement;

    return (
      <Flex dir="column" gap="none" grow={1}>
        <RedoButtonDropdown
          dataTestId="fulfillment-actions-dropdown"
          disabled={actionDisabled}
          dontRestrainIconWidth
          dropdownOpen={dropdownOpen}
          hierarchy="primary"
          IconTrailing={
            // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
            isPrintOption[dropdownItems[0].id as FulfillmentGroupActions] &&
            !printShortcutsDisabled
              ? () => ShortcutBadge
              : undefined
          }
          mainButtonRef={setActionButtonRef}
          mainButtonStretchHorizontal
          onClick={() => {
            // Log double-clicks during loading state
            if (loading || localLoading) {
              datadogLogs.logger.info(
                "Auto-purchase: action button clicked during loading",
                {
                  loading,
                  localLoading,
                  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                  action: dropdownItems[0].id,
                  source: "fulfillment-app",
                },
              );
            }

            onActionButtonClick?.();
            // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
            const action = dropdownItems[0].id as FulfillmentGroupActions;

            // Add 2-second loading state for print packing slip when it's the default action
            if (
              defaultActionButton ===
                FulfillmentGroupActions.PRINT_PACKING_SLIP &&
              action === FulfillmentGroupActions.PRINT_PACKING_SLIP
            ) {
              setLocalLoading(true);
              if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
              }
              loadingTimeoutRef.current = setTimeout(() => {
                setLocalLoading(false);
              }, 2000);
            }

            onClick(action);
          }}
          pending={loading || localLoading}
          setDropdownOpen={setDropdownOpen}
          size={buttonSize}
          squared={squared}
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          text={dropdownItems[0].text}
          theme={
            // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
            displayButtonTheme[dropdownItems[0].id as FulfillmentGroupActions]
          }
        >
          <RedoCommandMenu
            anchor={containerRef}
            dontRestrainIconWidth
            dropdownClassName={dropdownClassName}
            fitToAnchor={!dropdownClassName}
            items={dropdownItems}
            open={dropdownOpen}
            setOpen={setDropdownOpen}
            size="md"
          />
        </RedoButtonDropdown>
        <Flex px="md" w="full">
          <Flex ref={setContainerRef} w="full" />
        </Flex>
        {shouldShowUpsAgreementLink && (
          <Flex align="center" px="3xl" py="md">
            <Text
              cursor="pointer"
              fontSize="sm"
              fontWeight="bold"
              hoverTextColor="primary"
              onClick={() => setUpsAgreementModalOpen(true)}
              textColor="error"
              textDecoration="underline"
            >
              Please accept UPS Terms and Conditions
            </Text>
            <UpsAgreementModal
              isOpen={upsAgreementModalOpen}
              onAccepted={() => {
                setUpsAgreementModalOpen(false);
                reloadTeam?.();
              }}
              onClose={() => setUpsAgreementModalOpen(false)}
            />
          </Flex>
        )}
        {shouldShowFedexAgreementLink && (
          <Flex align="center" px="3xl" py="md">
            <Text
              cursor="pointer"
              fontSize="sm"
              fontWeight="bold"
              hoverTextColor="primary"
              onClick={() => setFedexAgreementModalOpen(true)}
              textColor="error"
              textDecoration="underline"
            >
              Please accept FedEx End User License Agreement
            </Text>
            <FedexAgreementModal
              isOpen={fedexAgreementModalOpen}
              onAccepted={() => {
                setFedexAgreementModalOpen(false);
                reloadTeam?.();
              }}
              onClose={() => setFedexAgreementModalOpen(false)}
            />
          </Flex>
        )}
        {marketplaceHoldReleaseUnsupported &&
          selectedStatus === FulfillmentGroupStatus.OnHold && (
            <Flex alignSelf="flex-start" pt="md">
              <RedoAlert
                color="warning"
                description="This hold must be released in the marketplace — it may take some time for that update to propagate back to Redo."
                size="sm"
                title="Release the hold to fulfill the order."
              />
            </Flex>
          )}
      </Flex>
    );
  },
);
